import {
  Module,
  VuexModule,
  Mutation,
  getModule,
} from "vuex-module-decorators";

import { Task, ScheduledTask, FocusedTask, copyTask } from "@/data/models/Task";

import { PlanningParameters } from "@/data/models/PlanningParameters";

import {
  TimelineSettings,
  TimelineVisibleArea,
  createTimelineConfigurationFromSettings,
  createTimelineSettingsIncludingDate,
} from "@/data/models/TimelineSettings";
import {
  Qualification,
  hasUserRequieredQualfications,
  getQualificationsOfUser,
} from "@/data/models/Qualification";
import { UserLookup } from "@/data/models/UserLookup";
import User, { Role } from "@/data/models/User";

import { TaskSortOrder } from "@/data/models/TaskSortOrder";
import store from "@/store";
import {
  getDefaultTaskDurationInMinutes,
  SchedulingSettings,
  createDefaultSchedulingSettings,
} from "@/data/models/SchedulingSettings";

import { ScheduledTasksLookup } from "@/utils/scheduling/TasksOfAllUsersLookup";

import * as _ from "lodash";
import {
  applyScheduledTaskFilter,
  applyTaskFilter,
  TaskFilter,
} from "@/data/models/TaskFilter";
import {
  calculateObjectiveFunctionRelativeToFocusedTask,
  SchedulingScore,
} from "@/utils/scheduling/SchedulingObjectiveFunction";

const name = "scheduling";

if (store.state[name]) {
  store.unregisterModule(name);
}

export interface UpdateTaskData {
  id: string;
  userId?: string;
  schedule?: {
    startDate: Date;
    endDate: Date;
  };
  showNote?: boolean;
  isMoving?: boolean;
  isTaskUrgent?: boolean;
  earliestStartDate?: Date;
  wasConfirmed?: boolean;
  updateScheduledTask?: boolean;
  durationInMin?: number;
  planningParameters?: PlanningParameters;
  score?: SchedulingScore;
  calculateScore?: boolean;
  note?: string | null;
}

export interface TaskStateBeforeEdit {
  id: string;
  focusedTask: FocusedTask;
  taskToSchedule: Task | null;
  scheduledTask: ScheduledTask | null;
}

@Module({ dynamic: true, namespaced: true, store, name: name })
export class SchedulingVuexModule extends VuexModule {
  openTasksSortOrder: TaskSortOrder = TaskSortOrder.EARLIEST_START_DATE;
  openTasksFilter: TaskFilter = TaskFilter.LAST_FOUR_WEEKS;

  confirmedTasksSortOrder: TaskSortOrder = TaskSortOrder.APPOINTMENT_DATE;
  confirmedTasksFilter: TaskFilter = TaskFilter.STARTING_FROM_TODAY;

  userLookup = new UserLookup([]);
  sortedUsers: User[] = [];
  userSortOrderLookup = new Map<string, number>();

  userQualificationLookup = new Map<string, Set<Qualification>>();
  highlightedTaskId: string | null = null;

  focusedTask: FocusedTask | null = null;
  taskStateBeforeEdit: TaskStateBeforeEdit | null = null;

  tasksToScheduleLookup = new Map<string, Task>();
  tasksToScheduleChangeTrigger = 0;

  scheduledTasksChangeTrigger = 0;

  timelineSettings: TimelineSettings = {
    startDate: new Date(),
    visibleArea: TimelineVisibleArea.TWO_WEEKS,
    showWeekends: false,
    onlyShowWorkingHours: true,
  };

  schedulingSettings = createDefaultSchedulingSettings();

  tasksOfAllUsersLookup = new ScheduledTasksLookup();

  @Mutation
  clear() {
    this.tasksOfAllUsersLookup.clear();
    this.tasksToScheduleLookup.clear();

    this.userQualificationLookup.clear();
    this.userLookup = new UserLookup([]);
    this.sortedUsers = [];

    this.focusedTask = null;
    this.taskStateBeforeEdit = null;

    this.scheduledTasksChangeTrigger = 0;
    this.scheduledTasksChangeTrigger = 0;
  }

  get tasksOfUsersLookup() {
    return this.tasksOfAllUsersLookup.tasksOfUsers;
  }

  get usersQualifiedForFocusedTask() {
    const focusedTask = this.focusedTask;

    if (!focusedTask) {
      return this.sortedUsers;
    }

    const requiredQualifications = focusedTask.requiredQualification;
    const userQualificationLookup = this.userQualificationLookup;

    return this.sortedUsers.filter((user) => {
      const qualificationsOfUser = userQualificationLookup.get(user.id);

      return hasUserRequieredQualfications(
        qualificationsOfUser,
        requiredQualifications
      );
    });
  }

  get unconfirmedTasks() {
    this.tasksToScheduleChangeTrigger;

    return [...this.tasksToScheduleLookup.values()].map(
      (task) => this.tasksOfAllUsersLookup.getScheduledTask(task.id) ?? task
    );
  }

  get unconfirmedTasksToDisplay() {
    return applyTaskFilter(this.unconfirmedTasks, this.openTasksFilter);
  }

  get scheduledTasks() {
    this.scheduledTasksChangeTrigger;

    return this.tasksOfAllUsersLookup.allScheduledTasks;
  }

  get scheduledTasksToDisplay() {
    return applyScheduledTaskFilter(
      this.scheduledTasks,
      this.confirmedTasksFilter,
      this.openTasksFilter
    );
  }

  get confirmedTasks(): ScheduledTask[] {
    this.scheduledTasksChangeTrigger;

    return this.tasksOfAllUsersLookup.allScheduledTasks.filter(
      (task) => task.wasConfirmed
    );
  }

  get confirmedTasksToDisplay() {
    this.scheduledTasksChangeTrigger;

    return applyScheduledTaskFilter(
      this.confirmedTasks,
      this.confirmedTasksFilter,
      this.openTasksFilter
    );
  }

  get timelineConfiguration() {
    const { planningBoardSettings } = this.schedulingSettings;

    return createTimelineConfigurationFromSettings(
      this.timelineSettings,
      planningBoardSettings
    );
  }

  get hasFocusedTaskDataChanged() {
    const focusedTask = this.focusedTask;
    const initialFocusedTask = this.taskStateBeforeEdit?.focusedTask;

    if (!initialFocusedTask || !focusedTask) {
      return false;
    }

    return (
      focusedTask.startDate !== initialFocusedTask.startDate ||
      focusedTask.endDate !== initialFocusedTask.endDate ||
      focusedTask.userId !== initialFocusedTask.userId ||
      focusedTask.durationInMin !== initialFocusedTask.durationInMin ||
      focusedTask.wasConfirmed !== initialFocusedTask.wasConfirmed ||
      focusedTask.note !== initialFocusedTask.note ||
      !_.isEqual(
        focusedTask.planningParameters,
        initialFocusedTask.planningParameters
      )
    );
  }

  @Mutation
  setEditEnabledForFocusedTask(editEnabled: boolean) {
    const focusedTask = this.focusedTask;

    if (focusedTask) {
      this.focusedTask = {
        ...focusedTask,
        editEnabled: editEnabled,
      };

      this.scheduledTasksChangeTrigger += 1;
      this.tasksToScheduleChangeTrigger += 1;
    }
  }

  @Mutation
  setSchedulingSettings(schedulingSettings: SchedulingSettings) {
    this.schedulingSettings = schedulingSettings;

    this.timelineSettings = {
      ...this.timelineSettings,
      visibleArea: schedulingSettings.planningBoardSettings.visibleArea,
    };

    for (const [, taskOfUser] of this.tasksOfAllUsersLookup.tasksOfUsers) {
      taskOfUser.setSchedulingSettings(schedulingSettings);
    }

    this.tasksToScheduleLookup.forEach((task) => {
      task.durationInMin = getDefaultTaskDurationInMinutes(
        task.taskType,
        schedulingSettings
      );
    });
  }

  @Mutation
  setTimelineSettings(timelineSettings: TimelineSettings) {
    this.timelineSettings = timelineSettings;
  }

  @Mutation
  setInitialData(data: { userLookup: UserLookup; tasksToSchedule: Task[] }) {
    this.openTasksSortOrder = TaskSortOrder.EARLIEST_START_DATE;
    this.openTasksFilter = TaskFilter.LAST_FOUR_WEEKS;

    this.confirmedTasksSortOrder = TaskSortOrder.APPOINTMENT_DATE;
    this.confirmedTasksFilter = TaskFilter.STARTING_FROM_TODAY;

    const { userLookup, tasksToSchedule } = data;

    this.userLookup = userLookup;

    tasksToSchedule.forEach((task) => {
      task.durationInMin = getDefaultTaskDurationInMinutes(
        task.taskType,
        this.schedulingSettings
      );

      if (!this.tasksOfAllUsersLookup.has(task.id)) {
        this.tasksToScheduleLookup.set(task.id, task);
      }
    });

    userLookup.users.forEach((user) => {
      const qualifications = getQualificationsOfUser(user);
      this.userQualificationLookup.set(user.id, new Set(qualifications));
    });

    const rolePriority = new Map([
      [Role.TECHNICIAN_AND_LEAK_DETECTION, 0],
      [Role.TECHNICIAN_2, 1],
      [Role.TECHNICIAN_1, 2],
      [Role.LEAK_DETECTION, 3],
      [Role.OFFICE, 4],
      [Role.PROJECT_MANAGER, 5],
      [Role.ADMIN, 6],
    ]);

    this.sortedUsers = this.userLookup.users
      .filter((user) => !user.deleted)
      .sort((a, b) => {
        const compRolePriority =
          (rolePriority.get(a.role ?? Role.ADMIN) ?? 0) -
          (rolePriority.get(b.role ?? Role.ADMIN) ?? 0);

        if (compRolePriority == 0) {
          return a.nickname.localeCompare(b.nickname);
        } else {
          return compRolePriority;
        }
      });

    this.sortedUsers.forEach((user, index) => {
      this.userSortOrderLookup.set(user.id, index);
    });

    this.tasksToScheduleChangeTrigger += 1;
  }

  @Mutation
  clearFocusedTask() {
    if (this.focusedTask) {
      this.highlightedTaskId = this.focusedTask.id;
    }

    this.taskStateBeforeEdit = null;
    this.focusedTask = null;
  }

  @Mutation
  updateOccupiedTimeDataForScheduledTasks() {
    this.tasksOfAllUsersLookup.fillTasksOfUserLookup(
      this.tasksOfAllUsersLookup.allScheduledTasks,
      this.userLookup,
      this.schedulingSettings
    );
  }

  @Mutation
  triggerListUpdates() {
    this.scheduledTasksChangeTrigger += 1;
    this.tasksToScheduleChangeTrigger += 1;
  }

  @Mutation
  setFocusedTask(newFocusedTask: FocusedTask) {
    this.highlightedTaskId = null;

    //Clear old scheduling score info
    newFocusedTask.schedulingScore = undefined;

    this.focusedTask = newFocusedTask;

    const { id, startDate } = newFocusedTask;
    const taskToSchedule = this.tasksToScheduleLookup.get(id);
    const scheduledTask = this.tasksOfAllUsersLookup.getScheduledTask(id);

    if (scheduledTask) {
      scheduledTask.schedulingScore = undefined;
    }

    this.taskStateBeforeEdit = {
      id,
      focusedTask: { ...newFocusedTask },
      taskToSchedule: taskToSchedule ? { ...taskToSchedule } : null,
      scheduledTask: scheduledTask ? { ...scheduledTask } : null,
    };

    if (startDate) {
      //Update timeline settings so that the focused tasks becomes visible
      const { planningBoardSettings } = this.schedulingSettings;
      const timelineSettings = this.timelineSettings;

      const timelineConfiguration = createTimelineConfigurationFromSettings(
        timelineSettings,
        planningBoardSettings
      );

      this.timelineSettings = createTimelineSettingsIncludingDate(
        startDate,
        timelineSettings,
        timelineConfiguration
      );
    }
  }

  @Mutation
  updateFocusedTask(updateTaskData: UpdateTaskData) {
    const {
      id,
      isMoving,
      userId: userIdUpdate,
      schedule: scheduleUpdate,
      wasConfirmed: wasConfirmedUpdate,
      durationInMin: durationInMinUpdate,
      updateScheduledTask,
      isTaskUrgent,
      earliestStartDate,
      planningParameters,
      note,
      showNote,
      score,
      calculateScore,
    } = updateTaskData;

    const focusedTask = this.focusedTask;

    if (focusedTask && focusedTask.id == id) {
      const focusedTaskUpdate: FocusedTask = {
        ...focusedTask,
        isMoving: isMoving ?? focusedTask.isMoving,
        showNote: showNote ?? focusedTask.showNote,
        planningParameters:
          planningParameters ?? focusedTask.planningParameters,
        userId: userIdUpdate ?? focusedTask.userId,
        startDate: scheduleUpdate?.startDate ?? focusedTask.startDate,
        endDate: scheduleUpdate?.endDate ?? focusedTask.endDate,
        wasConfirmed: wasConfirmedUpdate ?? focusedTask.wasConfirmed,
        urgent: isTaskUrgent ?? focusedTask.urgent,
        earliestStartDate: earliestStartDate ?? focusedTask.earliestStartDate,
        schedulingScore: score ?? focusedTask.schedulingScore,
        durationInMin: durationInMinUpdate ?? focusedTask.durationInMin,
        note: note === null ? undefined : note ? note : focusedTask.note,
      };

      const {
        startDate,
        endDate,
        userId,
        durationInMin,
        urgent,
      } = focusedTaskUpdate;

      if (startDate && endDate && userId) {
        const scheduledTask = this.tasksOfAllUsersLookup.getScheduledTask(id);
        const scheduledTaskUpdate = scheduledTask
          ? {
              ...scheduledTask,
              wasConfirmed: focusedTaskUpdate.wasConfirmed ?? false,
              durationInMin,
              startDate,
              endDate,
              userId,
              urgent,
              earliestStartDate: focusedTaskUpdate.earliestStartDate,
              planningParameters: focusedTaskUpdate.planningParameters,
              note: focusedTaskUpdate.note,
              schedulingScore: focusedTaskUpdate.schedulingScore,
            }
          : {
              ...focusedTaskUpdate,
              userId,
              startDate,
              endDate,
              durationInMin,
              wasConfirmed: false,
              planningParameters: focusedTaskUpdate.planningParameters,
              note: focusedTaskUpdate.note,
              schedulingScore: focusedTaskUpdate.schedulingScore,
            };

        if (calculateScore) {
          const score = calculateObjectiveFunctionRelativeToFocusedTask(
            scheduledTaskUpdate
          );

          scheduledTaskUpdate.schedulingScore = score;
          focusedTaskUpdate.schedulingScore = score;
        }

        this.tasksOfAllUsersLookup.updateScheduledTask(
          scheduledTask,
          scheduledTaskUpdate,
          this.userLookup,
          this.schedulingSettings
        );

        if (updateScheduledTask) {
          this.scheduledTasksChangeTrigger += 1;
        }
      }

      const taskToSchedule = this.tasksToScheduleLookup.get(id);

      if (taskToSchedule) {
        if (focusedTaskUpdate.wasConfirmed) {
          this.tasksToScheduleLookup.delete(id);
        } else {
          this.tasksToScheduleLookup.set(id, {
            ...taskToSchedule,
            durationInMin: durationInMin,
            planningParameters: focusedTaskUpdate.planningParameters,
            earliestStartDate: focusedTaskUpdate.earliestStartDate,
          });
        }

        if (updateScheduledTask) {
          this.tasksToScheduleChangeTrigger += 1;
        }
      }

      this.focusedTask = focusedTaskUpdate;
    }

    return false;
  }

  @Mutation
  clearSchedulingInfo(data: {
    taskId: string;
    clearFixedPlanningPlanningParameters?: boolean;
  }) {
    const { taskId, clearFixedPlanningPlanningParameters } = data;

    const focusedTask = this.focusedTask;

    if (focusedTask != null) {
      this.focusedTask = {
        ...focusedTask,
        startDate: undefined,
        endDate: undefined,
        userId: undefined,
        wasConfirmed: false,
        planningParameters: clearFixedPlanningPlanningParameters
          ? {
              ...focusedTask.planningParameters,
              isTargetDateFixed: false,
              isTargetTimeFixed: false,
              isTargetUserFixed: false,
            }
          : focusedTask.planningParameters,
      };
    }

    const scheduledTask = this.tasksOfAllUsersLookup.getScheduledTask(taskId);

    if (scheduledTask) {
      const scheduledTaskUpdate = {
        ...copyTask(scheduledTask),
        planningParameters: clearFixedPlanningPlanningParameters
          ? {
              ...scheduledTask.planningParameters,
              isTargetDateFixed: false,
              isTargetTimeFixed: false,
              isTargetUserFixed: false,
            }
          : scheduledTask.planningParameters,
      };

      this.tasksToScheduleLookup.set(taskId, copyTask(scheduledTaskUpdate));
      this.tasksOfAllUsersLookup.removeScheduledTask(scheduledTask);

      this.tasksToScheduleChangeTrigger += 1;
      this.scheduledTasksChangeTrigger += 1;
    }
  }

  @Mutation
  setScheduledTasks(scheduledTasks: ScheduledTask[]) {
    this.tasksOfAllUsersLookup.fillTasksOfUserLookup(
      scheduledTasks,
      this.userLookup,
      this.schedulingSettings
    );

    this.scheduledTasksChangeTrigger += 1;
  }

  @Mutation
  setScheduledTask(scheduledTask: ScheduledTask) {
    this.tasksOfAllUsersLookup.setScheduledTask(
      scheduledTask,
      this.userLookup,
      this.schedulingSettings
    );

    this.scheduledTasksChangeTrigger += 1;
  }

  @Mutation
  updateScheduledTask(data: {
    taskId: string;
    callback: (currentScheduledTask: ScheduledTask) => ScheduledTask;
  }) {
    const { taskId, callback } = data;

    const currentScheduledTask = this.tasksOfAllUsersLookup.getScheduledTask(
      taskId
    );

    if (currentScheduledTask) {
      this.tasksOfAllUsersLookup.setScheduledTask(
        callback(currentScheduledTask),
        this.userLookup,
        this.schedulingSettings
      );

      this.scheduledTasksChangeTrigger += 1;
      return true;
    }

    return false;
  }

  @Mutation
  setConfirmedTasksSortOrder(taskSortOrder: TaskSortOrder) {
    this.confirmedTasksSortOrder = taskSortOrder;
  }

  @Mutation
  setOpenTasksSortOrder(taskSortOrder: TaskSortOrder) {
    this.openTasksSortOrder = taskSortOrder;
  }

  @Mutation
  setConfirmedTasksFilter(scheduledTaskFilter: TaskFilter) {
    this.confirmedTasksFilter = scheduledTaskFilter;
  }

  @Mutation
  setOpenTasksFilter(taskFilter: TaskFilter) {
    this.openTasksFilter = taskFilter;
  }

  @Mutation
  removeTaskToSchedule(taskId: string) {
    this.tasksToScheduleLookup.delete(taskId);
    this.tasksToScheduleChangeTrigger += 1;
  }

  @Mutation
  undoConfirmationOfScheduledTask(taskId: string) {
    const scheduledTask = this.tasksOfAllUsersLookup.getScheduledTask(taskId);

    if (scheduledTask) {
      const update = {
        ...scheduledTask,
        planningParameters: {
          ...scheduledTask.planningParameters,
          isTargetDateFixed: false,
          isTargetTimeFixed: false,
          isTargetUserFixed: false,
        },
      };

      this.tasksToScheduleLookup.set(taskId, copyTask(update));

      this.tasksOfAllUsersLookup.setScheduledTask(
        {
          ...update,
          wasConfirmed: false,
        },
        this.userLookup,
        this.schedulingSettings
      );

      this.tasksToScheduleChangeTrigger += 1;
      this.scheduledTasksChangeTrigger += 1;
    }

    const focusedTask = this.focusedTask;

    if (focusedTask && focusedTask.id == taskId) {
      this.focusedTask = {
        ...focusedTask,
        wasConfirmed: false,
        planningParameters: {
          ...focusedTask.planningParameters,
          isTargetDateFixed: false,
          isTargetTimeFixed: false,
          isTargetUserFixed: false,
        },
      };
    }
  }

  @Mutation
  saveFocusedTaskChanges() {
    const focusedTask = this.focusedTask;

    if (focusedTask) {
      const taskId = focusedTask.id;

      const scheduledTask = this.tasksOfAllUsersLookup.getScheduledTask(taskId);

      if (scheduledTask) {
        this.scheduledTasksChangeTrigger++;
      }
    }

    //Todo persist changes
    this.taskStateBeforeEdit = null;
    this.focusedTask = null;
  }

  @Mutation
  removeScheduledTask(taskId: string) {
    const scheduledTask = this.tasksOfAllUsersLookup.getScheduledTask(taskId);

    if (scheduledTask) {
      this.tasksToScheduleLookup.set(taskId, copyTask(scheduledTask));
      this.tasksOfAllUsersLookup.removeScheduledTask(scheduledTask);

      this.tasksToScheduleChangeTrigger += 1;
      this.scheduledTasksChangeTrigger += 1;
    }
  }

  @Mutation
  restoreInitialDataOfFocusedTask() {
    const taskStateBeforeEdit = this.taskStateBeforeEdit;

    if (!taskStateBeforeEdit) {
      return;
    }

    const { id, taskToSchedule, scheduledTask } = taskStateBeforeEdit;

    if (taskToSchedule) {
      this.tasksToScheduleLookup.set(id, taskToSchedule);
    } else {
      this.tasksToScheduleLookup.delete(id);
    }

    if (scheduledTask) {
      this.tasksOfAllUsersLookup.setScheduledTask(
        scheduledTask,
        this.userLookup,
        this.schedulingSettings
      );
    } else {
      this.tasksOfAllUsersLookup.removeScheduledTaskWithId(id);
    }

    this.scheduledTasksChangeTrigger += 1;
    this.tasksToScheduleChangeTrigger += 1;
  }
}

export const SchedulingModule = getModule(SchedulingVuexModule, store);
